import { useState } from "react";
import { ChevronDown, ChevronRight, PenTool, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { StatusBadge } from "./WorkflowStatusBadge";
import { StepUsers } from "./WorkflowOverviewStepUsers";
import { UserResponseForm } from "./WorkflowOverviewStepResponseForm";
import { Card } from "~/atoms/card";
import { cn } from "~/lib/utils";
import { useUserContext } from "~/hooks/useUserContext";
import {
  type WorkflowStep,
  type WorkflowStepInstance,
  WorkflowStepUserResponses,
  WorkflowStepStatuses,
} from "~/api/types";
import { formatDate, formatDateRelative } from "~/helpers/date";
import { RichTextView } from "~/molecules/RichTextView";
import { StepComments } from "./WorkflowOverviewStepComments";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { DocumentArtifactStatus } from "~/config/types";
import {
  allUsersApproved,
  getStepComments,
  getLatestStepInstance,
} from "~/helpers/workflow-instances";

type StepContentProps = {
  step: WorkflowStep;
  stepInstances: WorkflowStepInstance[];
  artifactStatus: DocumentArtifactStatus;
  previousStepName?: string;
  onUserResponse: () => void;
};

export function WorkflowOverviewStep({
  step,
  stepInstances,
  artifactStatus,
  previousStepName,
  onUserResponse,
}: StepContentProps) {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [showResponseForm, setShowResponseForm] = useState(false);

  const isCompletedStep = step.status === WorkflowStepStatuses.completed;
  const allHaveApproved = allUsersApproved(step);
  const [isExpanded, setIsExpanded] = useState(!isCompletedStep);
  const [prevIsCompletedStep, setPrevIsCompletedStep] =
    useState(isCompletedStep);
  if (prevIsCompletedStep !== isCompletedStep) {
    setPrevIsCompletedStep(isCompletedStep);
    setIsExpanded(!isCompletedStep || allHaveApproved);
  }

  const currentUserInStep = step.users.find((su) => su.userId === user.id);
  const isActiveStep = step.status === WorkflowStepStatuses.active;
  const stepHasUsers = step.users.length > 0;
  const allUsersAcceptedActiveStep =
    allHaveApproved && isActiveStep && stepHasUsers;

  const hasAlreadyResponded =
    currentUserInStep &&
    currentUserInStep.responseStatus !== WorkflowStepUserResponses.pending;

  const isPublishedOrArchived =
    artifactStatus === DocumentArtifactStatus.published ||
    artifactStatus === DocumentArtifactStatus.archived;

  const canRespond =
    isActiveStep && currentUserInStep && !isPublishedOrArchived;
  const shouldShowForm = !hasAlreadyResponded || showResponseForm;

  const stepComments = getStepComments(stepInstances, step.id);

  return (
    <Card
      className={cn("gap-2 p-3", {
        "bg-info/4": isActiveStep,
        "bg-success/4":
          step.status === WorkflowStepStatuses.completed ||
          allUsersAcceptedActiveStep,
      })}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="line-clamp-2 min-w-0 font-medium wrap-break-word">
          {step.name}
        </h3>
        <div className="flex items-center">
          <StatusBadge status={step.status} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className="hover:text-foreground text-muted-foreground hover:bg-transparent"
          >
            {isExpanded ? (
              <ChevronDown className="size-5" />
            ) : (
              <ChevronRight className="size-5" />
            )}
          </Button>
        </div>
      </div>
      <TimeStamp step={step} stepInstances={stepInstances} />

      {isExpanded && (
        <>
          {step.type === "approval" && (
            <Badge variant="outline" className="bg-background self-start">
              <PenTool className="size-3" />
              {t(($) => $.commons.workflow.approval)}
            </Badge>
          )}

          {step.dueAt && (
            <div className="text-xs">
              {t(($) => $.commons.workflow.responseDate)}:{" "}
              {formatDate(step.dueAt)}
            </div>
          )}

          {step.description ? (
            <RichTextView
              value={step.description}
              className="text-muted-foreground text-sm"
            />
          ) : null}

          <StepUsers users={step.users} isActive={isActiveStep} />

          {stepComments.length > 0 && <StepComments comments={stepComments} />}

          {canRespond &&
            (shouldShowForm ? (
              <UserResponseForm
                stepId={step.id}
                instanceId={getLatestStepInstance(stepInstances, step.id)!.id}
                currentUserInStep={currentUserInStep}
                previousStepName={previousStepName}
                onUserResponse={async () => {
                  setShowResponseForm(false);
                  onUserResponse();
                }}
                stepType={step.type}
              />
            ) : (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => setShowResponseForm(true)}
                >
                  <Plus className="size-3" />
                  {t(($) => $.commons.workflow.respond.leaveAnotherResponse)}
                </Button>
              </div>
            ))}
        </>
      )}
    </Card>
  );
}

const TimeStamp = ({
  step,
  stepInstances,
}: {
  step: WorkflowStep;
  stepInstances: WorkflowStepInstance[];
}) => {
  const { t, i18n } = useTranslation();

  if (step.status === WorkflowStepStatuses.upcoming) return null;

  const latestInstance = getLatestStepInstance(stepInstances, step.id);
  if (!latestInstance) return null;

  switch (step.status) {
    case WorkflowStepStatuses.active:
      return (
        <div className="text-muted-foreground text-xs">
          {`${t(($) => $.commons.workflow.started)} ${formatDateRelative(latestInstance.createdAt, i18n.language)}`}
        </div>
      );
    case WorkflowStepStatuses.completed:
      if (!latestInstance.completedAt) return null;
      return (
        <div className="text-muted-foreground text-xs">
          {`${t(($) => $.commons.workflow.completed)} ${formatDateRelative(latestInstance.completedAt, i18n.language)}`}
        </div>
      );
    default:
      return null;
  }
};
